import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, ArrowLeft, Loader2, CheckCircle, Shield, AlertCircle, Eye, EyeOff, Copy, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  // For env-var update flow
  const [newPasswordHash, setNewPasswordHash] = useState('');
  const [instructions, setInstructions] = useState([]);
  const [copied, setCopied] = useState(false);

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setValidating(false);
        return;
      }
      
      try {
        await axios.get(`${API}/api/admin/validate-reset-token?token=${token}`);
        setTokenValid(true);
      } catch (err) {
        setTokenValid(false);
      } finally {
        setValidating(false);
      }
    };
    
    validateToken();
  }, [token]);

  const handleCopyHash = async () => {
    try {
      await navigator.clipboard.writeText(newPasswordHash);
      setCopied(true);
      toast.success('Hash copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    // Validate password strength
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    const hasLetter = /[a-zA-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    
    if (!hasLetter || !hasNumber) {
      setError('Password must contain at least one letter and one number');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/api/admin/reset-password`, {
        token,
        new_password: password
      });
      
      // Check if we got a hash back (env-var update flow)
      if (response.data.new_password_hash) {
        setNewPasswordHash(response.data.new_password_hash);
        setInstructions(response.data.instructions || []);
      }
      
      setSuccess(true);
      toast.success('Password reset processed!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to reset password. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Validating state
  if (validating) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center py-12 px-4">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-trust-blue mx-auto mb-4" />
          <p className="text-slate-400">Validating reset link...</p>
        </div>
      </div>
    );
  }

  // Invalid or missing token
  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500/20 rounded-2xl mb-4">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-white mb-2">
              Invalid Reset Link
            </h1>
            <p className="text-slate-400">
              This password reset link is invalid or has expired.
            </p>
          </div>

          <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
            <p className="text-slate-400 text-sm mb-6">
              Reset links expire after 30 minutes and can only be used once. 
              Please request a new reset link.
            </p>

            <div className="space-y-3">
              <Link to="/admin/forgot-password">
                <Button className="w-full bg-trust-blue hover:bg-trust-blue-hover text-white">
                  Request New Reset Link
                </Button>
              </Link>
              
              <Link to="/admin/login">
                <Button variant="outline" className="w-full border-slate-600 text-slate-300 hover:text-white">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Admin Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state with hash display
  if (success) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center py-12 px-4">
        <div className="max-w-lg w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 rounded-2xl mb-4">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-white mb-2">
              Password Reset Complete
            </h1>
            <p className="text-slate-400">
              {newPasswordHash 
                ? "Your new password hash has been generated."
                : "Your admin password has been successfully reset."
              }
            </p>
          </div>

          <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
            {/* If we have a hash to display (env-var update flow) */}
            {newPasswordHash ? (
              <>
                <div className="mb-6">
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-4">
                    <p className="text-amber-400 text-sm font-medium mb-2">
                      ⚠️ Important: Manual Environment Variable Update Required
                    </p>
                    <p className="text-slate-400 text-sm">
                      Copy the hash below and update your ADMIN_PASSWORD_HASH environment variable.
                    </p>
                  </div>
                  
                  <Label className="text-slate-300 text-sm">New Password Hash:</Label>
                  <div className="mt-2 relative">
                    <div className="bg-slate-900 border border-slate-600 rounded-lg p-3 pr-12 font-mono text-xs text-green-400 break-all">
                      {newPasswordHash}
                    </div>
                    <button
                      onClick={handleCopyHash}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-slate-700 rounded transition-colors"
                      data-testid="copy-hash-btn"
                    >
                      {copied ? (
                        <Check className="w-4 h-4 text-green-400" />
                      ) : (
                        <Copy className="w-4 h-4 text-slate-400" />
                      )}
                    </button>
                  </div>
                </div>

                {instructions.length > 0 && (
                  <div className="mb-6">
                    <Label className="text-slate-300 text-sm">Steps to complete:</Label>
                    <ol className="mt-2 space-y-2 text-sm text-slate-400">
                      {instructions.map((instruction, index) => (
                        <li key={index} className="flex gap-2">
                          <span className="text-trust-blue font-medium">{index + 1}.</span>
                          <span>{instruction.replace(/^\d+\.\s*/, '')}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                <div className="bg-slate-700/50 rounded-lg p-4 text-sm text-slate-400">
                  <p><strong className="text-white">Note:</strong> After updating the environment variable, restart the server and try logging in with your new password.</p>
                </div>
              </>
            ) : (
              <p className="text-slate-400 text-sm mb-6">
                You can now log in with your new password.
              </p>
            )}

            <div className="mt-6">
              <Button 
                className="w-full bg-trust-blue hover:bg-trust-blue-hover text-white"
                onClick={() => navigate('/admin/login')}
                data-testid="go-to-login-btn"
              >
                Go to Admin Login
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        {/* Admin Badge */}
        <div className="flex justify-center mb-6">
          <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30 px-4 py-1.5 text-sm">
            <Shield className="w-4 h-4 mr-2" />
            Admin Password Reset
          </Badge>
        </div>

        <div className="text-center mb-8">
          <img 
            src="/staypairhomes-logo.png" 
            alt="Stay Pair Homes" 
            className="h-16 w-auto mx-auto mb-4 object-contain"
          />
          <h1 className="font-heading text-3xl font-bold text-white mb-2">
            Create New Password
          </h1>
          <p className="text-slate-400">
            Enter a strong password for your admin account.
          </p>
        </div>

        <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-slate-300">New Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                  required
                  data-testid="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Confirm Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                  required
                  data-testid="confirm-password"
                />
              </div>
            </div>

            {/* Password requirements */}
            <div className="bg-slate-700/50 rounded-lg p-3">
              <p className="text-xs text-slate-400 mb-2">Password must:</p>
              <ul className="text-xs space-y-1">
                <li className={`flex items-center gap-2 ${password.length >= 8 ? 'text-green-400' : 'text-slate-500'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${password.length >= 8 ? 'bg-green-400' : 'bg-slate-500'}`}></span>
                  Be at least 8 characters
                </li>
                <li className={`flex items-center gap-2 ${/[a-zA-Z]/.test(password) ? 'text-green-400' : 'text-slate-500'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${/[a-zA-Z]/.test(password) ? 'bg-green-400' : 'bg-slate-500'}`}></span>
                  Contain at least one letter
                </li>
                <li className={`flex items-center gap-2 ${/[0-9]/.test(password) ? 'text-green-400' : 'text-slate-500'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${/[0-9]/.test(password) ? 'bg-green-400' : 'bg-slate-500'}`}></span>
                  Contain at least one number
                </li>
              </ul>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-trust-blue hover:bg-trust-blue-hover text-white h-12"
              disabled={loading}
              data-testid="reset-password-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Resetting Password...
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Reset Password
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-700">
            <Link to="/admin/login">
              <Button variant="ghost" className="w-full text-slate-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Admin Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminResetPasswordPage;
